import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { ScrollView, View, StyleSheet } from "react-native";
import { ClientType } from "@/types/db/clients";
import { TenantAddressType } from "@/types/db/tenants";
import useToast from "@/hooks/useToast";
import { InvoiceForm, InvoicePayload } from "./types";
import { InvoiceFields } from "./InvoiceFields";
import { FixedAttachment } from "@/components/Attachment/FixedAttachment";
import { AttachmentFile } from "@/components/Attachment/types";
import { ThemedText } from "@/components/ThemedText";
import { theme } from "@/constants/themes";
import { tenantService } from "@/services/tenantService";

export { InvoicePayload } from "./types";
export type { InvoiceForm } from "./types";

// Status options for dropdown
const STATUS_OPTIONS = [
  { text: "DRAFT", color: "todo" },
  { text: "SENT", color: "pending" },
  { text: "PAID", color: "complete" },
  { text: "OVERDUE", color: "attention" },
  { text: "CANCELLED", color: "neutral" },
] as const;

export function getInvoiceStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    DRAFT: colors.todo.border,
    SENT: colors.pending.border,
    PAID: colors.complete.border,
    OVERDUE: colors.attention.border,
    CANCELLED: colors.neutral.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export const INVOICE_STATUS_OPTIONS = STATUS_OPTIONS;

export interface InvoiceCreateFormProps {
  initialInvoice?: Partial<InvoiceForm>;
  fieldErrors?: { client_id?: string; project_id?: string };
  onSave?: (payload: { invoice: InvoiceForm }) => Promise<void> | void;
  onChange?: (payload: InvoicePayload) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onClientChange?: (client: ClientType | null) => void;
}

export interface InvoiceCreateFormRef {
  handleSave: () => Promise<void>;
  handleReset: () => void;
  updateInvoiceName: (name: string) => void;
  updateStatus: (status: string) => void;
  openClientModal: () => void;
  getPendingFiles: () => AttachmentFile[];
}

export const InvoiceCreateForm = forwardRef<InvoiceCreateFormRef, InvoiceCreateFormProps>(
  ({ initialInvoice, fieldErrors, onSave, onChange, onDirtyChange, onClientChange }, ref) => {
    const toast = useToast();
    const [selectedAddress, setSelectedAddress] = useState<TenantAddressType | null>(null);
    const [attachmentFiles, setAttachmentFiles] = useState<AttachmentFile[]>([]);
    const [addresses, setAddresses] = useState<TenantAddressType[]>([]);
    const [adjustmentAmount, setAdjustmentAmount] = useState(0);

    // Fetch tenant addresses on mount
    useEffect(() => {
      let cancel = false;
      (async () => {
        try {
          const metaData = await tenantService.getTenantMetaData();
          if (!cancel) setAddresses(metaData?.addresses ?? []);
        } catch (err) {
          console.error("Failed to fetch addresses:", err);
        }
      })();
      return () => {
        cancel = true;
      };
    }, []);

    // ---------- form state ----------
    const [invoice, setInvoice] = useState<InvoiceForm>({
      invoice_number: initialInvoice?.invoice_number ?? "",
      client_id: initialInvoice?.client_id ?? "",
      project_id: initialInvoice?.project_id ?? "",
      status: initialInvoice?.status ?? "DRAFT",
      meta_data: {
        ...initialInvoice?.meta_data,
        invoice_number: initialInvoice?.invoice_number,
        status: initialInvoice?.status ?? "DRAFT",
        line_items: initialInvoice?.meta_data?.line_items ?? [],
      },
    });

    useEffect(() => {
      onChange?.({ invoice });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice]);

    const hasInitialLineItems = (initialInvoice?.meta_data?.line_items?.length ?? 0) > 0;
    const baseline = useRef(hasInitialLineItems ? "" : JSON.stringify({ invoice }));
    const dirty = useMemo(() => JSON.stringify({ invoice }) !== baseline.current, [invoice]);

    // Notify parent of dirty state changes
    useEffect(() => {
      onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      handleSave,
      handleReset,
      updateInvoiceName: (name: string) => {
        setInvoice((i) => ({ ...i, invoice_number: name }));
      },
      updateStatus: (status: string) => {
        setInvoice((i) => ({
          ...i,
          status: status as any,
          meta_data: { ...i.meta_data, status: status as any, line_items: i.meta_data?.line_items ?? [] },
        }));
      },
      openClientModal: () => {
        // Client selection is now inline via ClientInput
      },
      getPendingFiles: () => attachmentFiles,
    }));

    // ---------- helpers ----------
    const updateInvoice = (patch: Partial<InvoiceForm>) => setInvoice((i) => ({ ...i, ...patch }));

    const handleSave = async () => {
      // Validations
      if (!invoice.client_id?.trim()) {
        toast.showToast({
          title: "Missing Client",
          message: "Please select a client for this invoice",
          toastType: "warning",
        });
        return;
      }
      if (!invoice.project_id?.trim()) {
        toast.showToast({
          title: "Missing Project",
          message: "Please select a project for this invoice",
          toastType: "warning",
        });
        return;
      }
      if (!invoice.status) {
        toast.showToast({
          title: "Missing Status",
          message: "Please select a status for this invoice",
          toastType: "warning",
        });
        return;
      }

      try {
        const baseItems = invoice.meta_data?.line_items ?? [];
        const finalItems =
          adjustmentAmount !== 0
            ? [
                ...baseItems,
                {
                  description: "Adjustment",
                  unit_price: adjustmentAmount,
                  quantity: 1,
                  amount: adjustmentAmount,
                },
              ]
            : baseItems;

        const finalInvoice: InvoiceForm = {
          ...invoice,
          meta_data: {
            ...invoice.meta_data,
            status: invoice.meta_data?.status ?? "DRAFT",
            line_items: finalItems,
          },
        };

        await onSave?.({ invoice: finalInvoice });
        baseline.current = JSON.stringify({ invoice });
      } catch (e: any) {
        throw e; // Re-throw to let parent handle it
      }
    };

    const handleReset = () => {
      const nextInvoice: InvoiceForm = {
        invoice_number: initialInvoice?.invoice_number ?? "",
        client_id: initialInvoice?.client_id ?? "",
        project_id: initialInvoice?.project_id ?? "",
        status: initialInvoice?.status ?? "DRAFT",
        meta_data: {
          ...initialInvoice?.meta_data,
          invoice_number: initialInvoice?.invoice_number,
          status: initialInvoice?.status ?? "DRAFT",
          line_items: initialInvoice?.meta_data?.line_items ?? [],
        },
      };
      setInvoice(nextInvoice);
      setSelectedAddress(null);
      setAttachmentFiles([]);
      setAdjustmentAmount(0);
      onClientChange?.(null);
      baseline.current = JSON.stringify({ invoice: nextInvoice });
    };

    return (
      <View style={styles.container}>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ minHeight: "100%", gap: 16 }}>
          <InvoiceFields
            invoice={invoice}
            fieldErrors={fieldErrors}
            selectedAddress={selectedAddress}
            addresses={addresses}
            updateInvoice={updateInvoice}
            adjustmentAmount={adjustmentAmount}
            onAdjustmentChange={setAdjustmentAmount}
            onAddressSelect={(address) => {
              setSelectedAddress(address);
              setInvoice((prev) => ({
                ...prev,
                meta_data: {
                  ...prev.meta_data,
                  status: prev.meta_data?.status ?? "DRAFT",
                  line_items: prev.meta_data?.line_items ?? [],
                  remittance_address_id: address.id,
                },
              }));
            }}
          />

          {/* Attachments Section */}
          <View style={{ gap: 8 }}>
            <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
              Attachments
            </ThemedText>
            <ThemedText style={{ fontSize: 12, opacity: 0.7 }}>
              Files will be uploaded when you save the invoice
            </ThemedText>
            <FixedAttachment
              files={attachmentFiles}
              onFilesChange={setAttachmentFiles}
              autoUpload={false}
              maxFiles={10}
              maxFileSize={50 * 1024 * 1024}
              mode="grid"
              showPreviews={true}
              dragDropEnabled={true}
              showUploadButton={true}
              placeholder="Add files to this invoice"
              testID="invoice-attachment"
            />
          </View>
        </ScrollView>
      </View>
    );
  },
);

InvoiceCreateForm.displayName = "InvoiceCreateForm";

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
